import { TreeViewNode } from 'models';


export interface TreeIndex {
	nodeMap: Map<string, TreeViewNode>;
	parentMap: Map<string, string | null>;
}

export const buildTreeIndex = (items: TreeViewNode[]): TreeIndex => {
	const nodeMap = new Map<string, TreeViewNode>();
	const parentMap = new Map<string, string | null>();
	const walk = (nodes: TreeViewNode[], parentId: string | null) => {
		nodes.forEach(n => {
			nodeMap.set(n.id, n);
			parentMap.set(n.id, parentId);
			if (n.children?.length) walk(n.children, n.id);
		});
	};
	walk(items, null);
	return { nodeMap, parentMap };
};


export const getAllDescendantIds = (
	index: TreeIndex,
	itemId: string
): string[] => {
	const node = index.nodeMap.get(itemId);
	if (!node?.children?.length) return [];
	const ids: string[] = [];
	const walk = (nodes: TreeViewNode[]) => {
		nodes.forEach(n => {
			ids.push(n.id);
			if (n.children?.length) walk(n.children);
		});
	};
	walk(node.children);
	return ids;
};

/** Collect ancestor IDs of a given node, ordered from nearest parent to root. */
export const getAllAncestorIds = (
	index: TreeIndex,
	itemId: string
): string[] => {
	const ancestors: string[] = [];
	let currentId = index.parentMap.get(itemId) ?? null;
	while (currentId !== null) {
		ancestors.push(currentId);
		currentId = index.parentMap.get(currentId) ?? null;
	}
	return ancestors;
};

export const propagateSelection = (
	index: TreeIndex,
	currentSelected: string[],
	toggledItemId: string,
	isSelected: boolean
): string[] => {
	const node = index.nodeMap.get(toggledItemId);
	if (!node) return currentSelected;

	const next = new Set(currentSelected);
	const descendants = getAllDescendantIds(index, toggledItemId);

	if (isSelected) {
		next.add(toggledItemId);
		descendants.forEach(id => next.add(id));

		const ancestors = getAllAncestorIds(index, toggledItemId);
		ancestors.every(ancestorId => {
			const ancestor = index.nodeMap.get(ancestorId);
			const allChildrenSelected =
				!!ancestor?.children?.every(c => next.has(c.id));
			if (allChildrenSelected) {
				next.add(ancestorId);
				return true;
			}
			return false;
		});
	} else {
		next.delete(toggledItemId);
		descendants.forEach(id => next.delete(id));
		getAllAncestorIds(index, toggledItemId).forEach(id => next.delete(id));
	}

	return Array.from(next);
};
